/**
 * useAgenticProcessStream
 *
 * Subscribe to `AgenticProcess.flowDataStream.items` with a stable snapshot
 * — the React-18-correct way to consume the per-process stream that the
 * worker drivers feed (`source=stream` for live, `source=history` for
 * replay, `source=sniffer` for forwarded hook events).
 *
 * Why `useSyncExternalStore` and not `useEntityData`: the latter calls
 * `setFlowData([...items])` on every WS event, recreating the array
 * reference even when the content didn't change. That ref churn cascades
 * through downstream `useMemo`s and historically caused render loops in the
 * InteractiveTerminal trace gutter (Tooltip ref-merge → setState every
 * commit). `useSyncExternalStore` lets us return a memoized snapshot that
 * only re-emits when items actually changed (length or per-item identity).
 */

import { AgenticProcess, FlowData, FlowElementTypes } from '@sdk';
import { useHistoryLoadAlert } from '@src/hooks/use-history-load-alert';
import { retireObservedEchoes } from '@src/hooks/retire-observed-echoes';
import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';

export function useAgenticProcessStream(process: AgenticProcess | null): FlowData[] {
  // Every surface that renders the stream comes through here, so this is the one
  // place that sees "the stream is empty" — including the case where it is empty
  // because the replay was DROPPED rather than because nothing was said. Raising
  // the alert here covers each of those surfaces once, instead of asking every
  // caller to remember to wire it (FLOWPAD-2038). `notify` dedupes per process,
  // so several surfaces observing the same process still yield one toast.
  useHistoryLoadAlert(process);

  // `rawRef` mirrors the stream for the change check; `snapshotRef` is what
  // surfaces render — the same rows minus the optimistic user echoes a live
  // observation has superseded (see `retireObservedEchoes`: Copilot's stdout
  // echoes the user turn, so without this its message rendered twice mid-turn).
  const rawRef = useRef<FlowData[]>([]);
  const snapshotRef = useRef<FlowData[]>([]);

  const subscribe = useCallback((cb: () => void) => {
    if (!process) return () => {};
    const onData = () => cb();
    const onClear = () => cb();
    process.flowDataStream.on('data', onData);
    process.flowDataStream.on('clear', onClear);
    return () => {
      process.flowDataStream.off('data', onData);
      process.flowDataStream.off('clear', onClear);
    };
  }, [process]);

  const getSnapshot = useCallback(() => {
    if (!process) {
      if (rawRef.current.length !== 0) rawRef.current = [];
      if (snapshotRef.current.length !== 0) snapshotRef.current = [];
      return snapshotRef.current;
    }
    const items = process.flowDataStream.items as FlowData[];
    if (
      items.length !== rawRef.current.length ||
      items.some((v, i) => v !== rawRef.current[i])
    ) {
      rawRef.current = [...items];
      // Arrival order, not the timestamp-sorted `items`: see retireObservedEchoes.
      snapshotRef.current = retireObservedEchoes(rawRef.current, process.flowDataStream.ownItems);
    }
    return snapshotRef.current;
  }, [process]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** How many tools a process has called so far — the "steps" a running agent shows. */
export function useProcessToolCount(process: AgenticProcess | null): number {
  const items = useAgenticProcessStream(process);
  return useMemo(() => items.filter((fd) => fd.elementType === FlowElementTypes.TOOL_CALL).length, [items]);
}
